"use client"

import { useState, useEffect } from "react"
import { Box, Typography, Grid, Avatar, ClickAwayListener, Chip, Tooltip } from "@mui/material"
import {
  DragIndicator as DragIcon,
  ExpandMore as ExpandMoreIcon,
  AccountTree as DependencyIcon,
} from "@mui/icons-material"
import MoreActionsMenu from "./MoreActions"
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const BacklogIssue = ({
  issue,
  onDragStart,
  onNavigate,
  issueTypes,
  statuses,
  priorities,
  getInitials,
  onParentChange,
  onAssigneeChange,
  onStatusChange,
  assignees = [],
  projectId,
  onMenuAction,
  projectKey,
  onMove,
  onDelete,
  onIssueUpdated,
  dependencyInfo = { blocks: [], blockedBy: [], relatesTo: [] },
}) => {
  const [isAssigneeDropdownOpen, setIsAssigneeDropdownOpen] = useState(false)
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false)
  const [teamMembers, setTeamMembers] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentAssignee, setCurrentAssignee] = useState(issue.assigneeName || issue.assignee || "Unassigned")

  // Fetch team members when component mounts
  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!projectId) return

      try {
            const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
        setLoading(true)
        const response = await fetch(`${API_URL}/api/addteam/project/${projectId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        )
        const data = await response.json()

        // Handle different response structures
        let allMembers = []

        if (data.teamMembers) {
          // If teamMembers is an array of arrays or contains multiple arrays
          if (Array.isArray(data.teamMembers)) {
            if (data.teamMembers.length > 0 && Array.isArray(data.teamMembers[0])) {
              // If it's an array of arrays, flatten it
              allMembers = data.teamMembers.flat()
            } else {
              // If it's a simple array
              allMembers = data.teamMembers
            }
          }

          // If response has separate arrays (like developers, testers, etc.)
          if (data.developers && Array.isArray(data.developers)) {
            allMembers = [...allMembers, ...data.developers]
          }
          if (data.testers && Array.isArray(data.testers)) {
            allMembers = [...allMembers, ...data.testers]
          }
          if (data.managers && Array.isArray(data.managers)) {
            allMembers = [...allMembers, ...data.managers]
          }

          setTeamMembers(allMembers)
        } else {
          setTeamMembers([])
        }
      } catch (error) {
        setTeamMembers([])
      } finally {
        setLoading(false)
      }
    }

    fetchTeamMembers()
  }, [projectId])

  // Enhanced assignee resolution logic
  useEffect(() => {
    
    
    // Enhanced logic to resolve assignee display name
    let resolvedAssignee = "Unassigned"
    
    // Priority 1: Use assigneeName if available
    if (issue.assigneeName && issue.assigneeName !== "Unassigned") {
      resolvedAssignee = issue.assigneeName
    }
    // Priority 2: Use assignee if available and not just an ID
    else if (issue.assignee && issue.assignee !== "Unassigned" && isNaN(issue.assignee)) {
      resolvedAssignee = issue.assignee
    }
    // Priority 3: Try to resolve assignee_id to name from team members
    else if (issue.assignee_id && teamMembers.length > 0) {
      const assigneeMember = teamMembers.find(
        member => (member.employee_id || member.id) === issue.assignee_id
      )
      if (assigneeMember) {
        resolvedAssignee = assigneeMember.name || assigneeMember.username || assigneeMember.email || "Unassigned"
      }
    }
    // Priority 4: Try to resolve numeric assignee to name from team members
    else if (issue.assignee && !isNaN(issue.assignee) && teamMembers.length > 0) {
      const assigneeMember = teamMembers.find(
        member => (member.employee_id || member.id) == issue.assignee
      )
      if (assigneeMember) {
        resolvedAssignee = assigneeMember.name || assigneeMember.username || assigneeMember.email || "Unassigned"
      }
    }
    
    setCurrentAssignee(resolvedAssignee)
  }, [issue.assigneeName, issue.assignee, issue.assignee_id, issue.id, teamMembers]) // Added teamMembers as dependency

  // Helper components
  const IssueTypeIcon = ({ type }) => {
    const issueType = issueTypes.find((t) => t.name === type || t.id === type)
    return issueType ? issueType.icon : null
  }

  const PriorityIcon = ({ priority }) => {
    const pri = priorities.find((p) => p.name === priority || p.id === priority)
    return pri ? pri.icon : null
  }

  // Get issue type for tooltip and text
  const getIssueTypeInfo = (type) => {
    return issueTypes.find((t) => {
      const typeStr = String(type).toLowerCase();
      return t.name.toLowerCase() === typeStr || t.id === parseInt(type);
    });
  }

  // Fixed render dependency chips - now shows correct link types from each issue's perspective
  const renderDependencyChips = () => {
    const chips = []
    const processedDependencies = new Set()

    // Helper function to create a unique key for each dependency
    const createDependencyKey = (sourceId, targetId) => {
      return `${Math.min(sourceId, targetId)}-${Math.max(sourceId, targetId)}`
    }

    // Show "blocks" chips (this issue blocks others)
    if (dependencyInfo.blocks && dependencyInfo.blocks.length > 0) {
      dependencyInfo.blocks.forEach((dep, index) => {
        const dependencyKey = createDependencyKey(issue.id, dep.issue.id)

        if (!processedDependencies.has(dependencyKey)) {
          processedDependencies.add(dependencyKey)

          // Use the exact linkType from the dependency
          const linkTypeText = dep.linkType || "blocks"

          chips.push(
            <Tooltip
              key={`blocks-${dep.issue.id}`}
              title={`${linkTypeText} ${dep.issue.key}: ${dep.issue.summary}`}
              arrow
            >
              <Chip
                size="small"
                icon={<DependencyIcon sx={{ fontSize: "12px !important" }} />}
                label={`${linkTypeText} ${dep.issue.key}`}
                sx={{
                  height: 18,
                  fontSize: "9px",
                  bgcolor: "#e3f2fd",
                  color: "#1976d2",
                  border: "1px solid #bbdefb",
                  mr: 0.5,
                  mb: 0.5,
                  "& .MuiChip-icon": {
                    fontSize: "10px",
                    color: "#1976d2",
                  },
                  "& .MuiChip-label": {
                    px: 0.5,
                  },
                }}
              />
            </Tooltip>,
          )
        }
      })
    }

    // Show "blocked by" chips (this issue is blocked by others)
    if (dependencyInfo.blockedBy && dependencyInfo.blockedBy.length > 0) {
      dependencyInfo.blockedBy.forEach((dep, index) => {
        const dependencyKey = createDependencyKey(issue.id, dep.issue.id)

        if (!processedDependencies.has(dependencyKey)) {
          processedDependencies.add(dependencyKey)

          // Use the exact linkType from the dependency
          const linkTypeText = dep.linkType || "blocked by"

          chips.push(
            <Tooltip
              key={`blockedBy-${dep.issue.id}`}
              title={`${linkTypeText} ${dep.issue.key}: ${dep.issue.summary}`}
              arrow
            >
              <Chip
                size="small"
                icon={<DependencyIcon sx={{ fontSize: "12px !important" }} />}
                label={`${linkTypeText} ${dep.issue.key}`}
                sx={{
                  height: 18,
                  fontSize: "9px",
                  bgcolor: "#fff3e0",
                  color: "#f57c00",
                  border: "1px solid #ffcc02",
                  mr: 0.5,
                  mb: 0.5,
                  "& .MuiChip-icon": {
                    fontSize: "10px",
                    color: "#f57c00",
                  },
                  "& .MuiChip-label": {
                    px: 0.5,
                  },
                }}
              />
            </Tooltip>,
          )
        }
      })
    }

    // Show "relates to" chips (this issue relates to others)
    if (dependencyInfo.relatesTo && dependencyInfo.relatesTo.length > 0) {
      dependencyInfo.relatesTo.forEach((dep, index) => {
        const dependencyKey = createDependencyKey(issue.id, dep.issue.id)

        if (!processedDependencies.has(dependencyKey)) {
          processedDependencies.add(dependencyKey)

          chips.push(
            <Tooltip
              key={`relatesTo-${dep.issue.id}`}
              title={`relates to ${dep.issue.key}: ${dep.issue.summary}`}
              arrow
            >
              <Chip
                size="small"
                icon={<DependencyIcon sx={{ fontSize: "12px !important" }} />}
                label={`relates to ${dep.issue.key}`}
                sx={{
                  height: 18,
                  fontSize: "9px",
                  bgcolor: "#f3e5f5",
                  color: "#9c27b0",
                  border: "1px solid #ce93d8",
                  mr: 0.5,
                  mb: 0.5,
                  "& .MuiChip-icon": {
                    fontSize: "10px",
                    color: "#9c27b0",
                  },
                  "& .MuiChip-label": {
                    px: 0.5,
                  },
                }}
              />
            </Tooltip>,
          )
        }
      })
    }

    return chips
  }

  // Enhanced Status Badge component with dropdown and API integration
  const StatusDropdown = ({ status }) => {
    const statusObj = statuses.find((s) => s.name === status || s.id === status)
    const currentStatus = statusObj || { name: status || "TO DO", color: "#ede54e", textColor: "#172B4D" }

    const handleStatusChange = async (newStatusValue) => {
      const newStatus = statuses.find((s) => s.id === newStatusValue || s.name === newStatusValue)

      if (!newStatus) return

      try {
            const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
        // Call API to update status
        const response = await fetch(`${API_URL}/api/issues/${issue.id}/status`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            status: newStatus.id,
          }),
        })

        const data = await response.json()

        if (data.success) {
          // Call parent component's status change handler if provided
          if (onStatusChange) {
            onStatusChange(issue.id, newStatus)
          }
          if (onIssueUpdated) {
        onIssueUpdated({ ...issue, status: newStatus.name || newStatus });
      }

        } else {
        }
      } catch (error) {
      }

      setIsStatusDropdownOpen(false)
    }

    const handleStatusClick = (event) => {
      event.stopPropagation() // Prevent triggering the row click
      setIsStatusDropdownOpen(!isStatusDropdownOpen)
    }

    return (
      <ClickAwayListener onClickAway={() => setIsStatusDropdownOpen(false)}>
        <Box sx={{ position: "relative" }}>
          <Box
            onClick={handleStatusClick}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              backgroundColor: currentStatus.color,
              color: currentStatus.textColor,
              fontWeight: 500,
              fontSize: "0.75rem",
              padding: "2px 8px",
              borderRadius: "3px",
              whiteSpace: "nowrap",
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
              },
            }}
          >
            {currentStatus.name}
            <ExpandMoreIcon sx={{ fontSize: "14px", ml: 0.5 }} />
          </Box>

          {isStatusDropdownOpen && (
            <Box
              sx={{
                position: "absolute",
                top: "100%",
                left: "50%",
                transform: "translateX(-50%)",
                backgroundColor: "white",
                border: "1px solid #ddd",
                borderRadius: "4px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                zIndex: 1000,
                minWidth: "120px",
                maxHeight: "200px",
                overflowY: "auto",
                mt: 0.5,
              }}
            >
              {statuses.map((statusOption) => (
                <Box
                  key={statusOption.id}
                  onClick={() => handleStatusChange(statusOption.id || statusOption.name)}
                  sx={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "2px",
                      backgroundColor: statusOption.color,
                      mr: 1,
                    }}
                  />
                  <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                    {statusOption.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </ClickAwayListener>
    )
  }

  // Enhanced Assignee component with dropdown and API integration
  const AssigneeDropdown = ({ assignee }) => {
    const handleAssigneeChange = async (selectedMember) => {
      try {
        // Determine what to send to the backend
        let assigneeToSend = null
        let displayName = "Unassigned"

        if (selectedMember !== "Unassigned") {
          if (typeof selectedMember === "object") {
            // If selectedMember is a member object, use employee_id
            assigneeToSend = selectedMember.employee_id || selectedMember.id
            displayName = selectedMember.name || selectedMember.username || selectedMember.email
          } else {
            // If selectedMember is a string (name), find the corresponding member
            const memberObj = teamMembers.find(
              (member) => (member.name || member.username || member.email) === selectedMember,
            )
            if (memberObj) {
              assigneeToSend = memberObj.employee_id || memberObj.id
              displayName = selectedMember
            } else {
              // Fallback for legacy assignees that might not have employee_id
              assigneeToSend = selectedMember
              displayName = selectedMember
            }
          }
        }

        // Update local state immediately for instant UI feedback
        setCurrentAssignee(displayName)
const token = localStorage.getItem("token"); // get JWT token
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
        // Call API to update assignee with employee_id
        const response = await fetch(`${API_URL}/api/issues/${issue.id}/assignee`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            assignee: assigneeToSend, // Send employee_id or null
          }),
        })

        const data = await response.json()

        if (data.success) {
          // Call parent component's assignee change handler if provided
          if (onAssigneeChange) {
            onAssigneeChange(issue.id, displayName)
          }
if (onIssueUpdated) {
        onIssueUpdated({ 
          ...issue, 
          assignee: assigneeToSend,
          assigneeName: displayName,
          // assigneeDetails: assigneeDetails 
        });
      }
        } else {
          // Revert local state on failure
          setCurrentAssignee(issue.assigneeName || issue.assignee || "Unassigned")
        }
      } catch (error) {
        // Revert local state on failure
        setCurrentAssignee(issue.assigneeName || issue.assignee || "Unassigned")
      }

      setIsAssigneeDropdownOpen(false)
    }

    const handleAssigneeClick = (event) => {
      event.stopPropagation()
      setIsAssigneeDropdownOpen(!isAssigneeDropdownOpen)
    }

    const displayAssignee = currentAssignee && currentAssignee !== "Unassigned" ? currentAssignee : "Unassigned"

    return (
      <ClickAwayListener onClickAway={() => setIsAssigneeDropdownOpen(false)}>
        <Box sx={{ position: "relative" }}>
          <Box
            onClick={handleAssigneeClick}
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              position: "relative",
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
              },
            }}
          >
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: "0.75rem",
                bgcolor: displayAssignee !== "Unassigned" ? "#0052CC" : "#DFE1E6",
                color: displayAssignee !== "Unassigned" ? "#FFFFFF" : "#172B4D",
              }}
            >
              {getInitials(displayAssignee)}
            </Avatar>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                ml: 1,
                display: { xs: "none", sm: "block" },
                maxWidth: "80px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayAssignee}
            </Typography>
            <ExpandMoreIcon sx={{ fontSize: "14px", ml: 0.5, display: { xs: "none", sm: "block" } }} />
          </Box>

          {isAssigneeDropdownOpen && (
            <Box
              sx={{
                position: "absolute",
                top: "100%",
                right: 0,
                backgroundColor: "white",
                border: "1px solid #ddd",
                borderRadius: "8px",
                boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                zIndex: 1000,
                minWidth: "280px",
                maxWidth: "320px",
                maxHeight: "400px",
                overflowY: "auto",
                mt: 0.5,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#c1c1c1",
                  borderRadius: "3px",
                  "&:hover": {
                    background: "#a8a8a8",
                  },
                },
              }}
            >
              {/* Unassigned option */}
              <Box
                onClick={() => handleAssigneeChange("Unassigned")}
                sx={{
                  padding: "12px 16px",
                  cursor: "pointer",
                  borderBottom: "1px solid #f0f0f0",
                  "&:hover": {
                    backgroundColor: "#f8f9fa",
                  },
                  display: "flex",
                  alignItems: "center",
                  minHeight: "48px",
                  backgroundColor: displayAssignee === "Unassigned" ? "#e3f2fd" : "transparent",
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: "0.875rem",
                    bgcolor: "#DFE1E6",
                    color: "#172B4D",
                    mr: 2,
                    flexShrink: 0,
                  }}
                >
                  {getInitials("Unassigned")}
                </Avatar>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: displayAssignee === "Unassigned" ? 600 : 500,
                    color: "#333",
                  }}
                >
                  Unassigned
                </Typography>
              </Box>

              {/* Team members from API */}
              {loading ? (
                <Box sx={{ padding: "20px", textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading team members...
                  </Typography>
                </Box>
              ) : (
                teamMembers.length > 0 &&
                teamMembers.map((member, index) => {
                  const memberName = member.name || member.username || member.email || `Member ${index + 1}`
                  const memberId = member.employee_id || member.id || `${memberName}-${index}`
                  const isSelected = displayAssignee === memberName

                  return (
                    <Box
                      key={memberId}
                      onClick={() => handleAssigneeChange(member)} // Pass the entire member object
                      sx={{
                        padding: "12px 16px",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#f8f9fa",
                        },
                        display: "flex",
                        alignItems: "center",
                        borderBottom: index < teamMembers.length - 1 ? "1px solid #f0f0f0" : "none",
                        minHeight: "52px",
                        backgroundColor: isSelected ? "#e3f2fd" : "transparent",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: "0.875rem",
                          bgcolor: "#0052CC",
                          color: "#FFFFFF",
                          mr: 2,
                          flexShrink: 0,
                        }}
                      >
                        {getInitials(memberName)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: isSelected ? 600 : 500,
                            color: "#333",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            lineHeight: "1.3",
                          }}
                        >
                          {memberName}
                        </Typography>
                        {member.role && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.75rem",
                              color: "#666",
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              lineHeight: "1.2",
                              mt: 0.5,
                            }}
                          >
                            {member.role}
                          </Typography>
                        )}
                        {/* Show employee ID for debugging (optional) */}
                        {(member.employee_id || member.id) && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.7rem",
                              color: "#999",
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              lineHeight: "1.2",
                            }}
                          >
                            ID: {member.employee_id || member.id}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )
                })
              )}

              {/* Fallback assignees if provided via props */}
              {assignees.length > 0 &&
                assignees.map((assigneeOption, index) => {
                  const assigneeName = assigneeOption.name || assigneeOption
                  // Skip if already in team members
                  if (teamMembers.some((member) => (member.name || member.username || member.email) === assigneeName)) {
                    return null
                  }

                  const isSelected = displayAssignee === assigneeName

                  return (
                    <Box
                      key={assigneeOption.id || `fallback-${assigneeName}-${index}`}
                      onClick={() => handleAssigneeChange(assigneeOption)} // Pass the entire object
                      sx={{
                        padding: "12px 16px",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#f8f9fa",
                        },
                        display: "flex",
                        alignItems: "center",
                        borderBottom: "1px solid #f0f0f0",
                        minHeight: "48px",
                        backgroundColor: isSelected ? "#e3f2fd" : "transparent",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: "0.875rem",
                          bgcolor: "#0052CC",
                          color: "#FFFFFF",
                          mr: 2,
                          flexShrink: 0,
                        }}
                      >
                        {getInitials(assigneeName)}
                      </Avatar>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: isSelected ? 600 : 500,
                          color: "#333",
                        }}
                      >
                        {assigneeName}
                      </Typography>
                    </Box>
                  )
                })}

              {/* Show message if no team members found */}
              {!loading && teamMembers.length === 0 && assignees.length === 0 && (
                <Box sx={{ padding: "20px", textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    No team members found
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </ClickAwayListener>
    )
  }

  // Handle menu actions
  const handleMenuAction = (action, issue, data) => {
    if (onMenuAction) {
      onMenuAction(action, issue, data)
    }
  }

  // Handle assignee change from menu
  const handleMenuAssigneeChange = (issueId, newAssignee) => {
    setCurrentAssignee(newAssignee)
    if (onAssigneeChange) {
      onAssigneeChange(issueId, newAssignee)
    }
  }

  const handleMenuParentChange = (issueId, newParent) => {
    // You can add any local state updates here if needed
    // If you have a parent callback, call it here
    if (onParentChange) {
      onParentChange(issueId, newParent)
    }
  }

  return (
    <Box
      draggable
      onDragStart={() => onDragStart(issue.id)}
onClick={() => {
        if (typeof onNavigate === 'function') {
          onNavigate(issue);
        }
      }}      sx={{
        cursor: "pointer",
        position: "relative", // Add this to establish stacking context
        "&:hover": { bgcolor: "#f4f5f7" },
      }}
    >
     <Grid
  container
  alignItems="center"
  sx={{
    py: 1.5,
    px: 2,
    borderBottom: "1px solid #f0f0f0",
  }}
>
  <Grid item xs={4} sm={3} md={4} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <DragIcon fontSize="small" sx={{ color: "text.disabled", mr: 0.5, flexShrink: 0 }} />
    <Tooltip
      title={(() => {
        const issueType = getIssueTypeInfo(issue.issueType || issue.type);
        return issueType ? `${issueType.name}` : 'Unknown Issue Type';
      })()}
      arrow
      placement="top"
    >
      <Box sx={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}>
        <IssueTypeIcon type={issue.issueType || issue.type} />
      </Box>
    </Tooltip>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="body2"
        fontWeight="500"
        color="text.primary"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          mb: 0.5,
        }}
      >
        {issue.summary}
      </Typography>
      {/* Dependency chips */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>{renderDependencyChips()}</Box>
    </Box>
  </Grid>

  <Grid item xs={2} sm={1} md={1} sx={{ textAlign: "center" }}>
    {(() => {
      const issueType = getIssueTypeInfo(issue.issueType || issue.type);
      if (issueType) {
        return (
          <Tooltip title={issueType.description || issueType.name} arrow placement="top">
            <Chip
              label={issueType.name}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.7rem",
                backgroundColor: issueType.color,
                color: "white",
                fontWeight: 500,
              }}
            />
          </Tooltip>
        );
      }
      return (
        <Chip
          label="Unknown"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.7rem",
            backgroundColor: "#ccc",
            color: "white",
            fontWeight: 500,
          }}
        />
      );
    })()}
  </Grid>

  <Grid item xs={2} sm={2} md={2} sx={{ textAlign: "center" }}>
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight="500"
      sx={{
        padding: "2px 6px",
        borderRadius: "3px",
        backgroundColor: "#DFE1E6",
      }}
    >
      {issue.key}
    </Typography>
  </Grid>

  <Grid item xs={2} sm={2} md={2} sx={{ textAlign: "center" }}>
    <AssigneeDropdown assignee={currentAssignee} />
  </Grid>

  <Grid item xs={1} sm={2} md={2} sx={{ textAlign: "center" }}>
    <StatusDropdown status={issue.status} />
  </Grid>

  <Grid item xs={1} sm={2} md={1} sx={{ textAlign: "center" }}>
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
      {/* Priority Icon and Name */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <PriorityIcon priority={issue.priority} />
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: { xs: "none", md: "block" } }}>
          {(() => {
            const priority = priorities.find((p) => p.name === issue.priority || p.id === issue.priority)
            return priority ? priority.name : issue.priority || "Medium"
          })()}
        </Typography>
      </Box>

      {/* More Actions Menu */}
      <MoreActionsMenu
        issue={issue}
        onAssigneeChange={handleMenuAssigneeChange}
        teamMembers={teamMembers}
        getInitials={getInitials}
        onAction={handleMenuAction}
        projectId={projectId}
        onParentChange={handleMenuParentChange}
        projectKey={projectKey}
        onMove={onMove}
        onDelete={onDelete}
        projectDetails={{ key: projectKey }}
      />
    </Box>
  </Grid>
</Grid>
    </Box>
  )
}

export default BacklogIssue